import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  Search, Filter, RefreshCw, Download, ChevronDown, ChevronLeft, ChevronRight,
  Calendar, User, Shield, Smartphone, Globe, AlertTriangle, CheckCircle2,
  XCircle, Eye, X, Clock, Activity, Server, Users, Car, Wallet, Settings,
  FileText, AlertCircle, Info, Database, Radio, Pause, Play, Archive,
  Zap, Bell, Wifi, WifiOff
} from 'lucide-react';
import api from '../../services/api';
import { format, parseISO, subDays } from 'date-fns';
import { sq } from 'date-fns/locale';
import { toast } from 'sonner';

const AuditLogsPage = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0, total_pages: 0 });
  const [stats, setStats] = useState(null);
  
  // Tabs: recent, live-feed, archive
  const [activeTab, setActiveTab] = useState('recent');
  
  // Live Feed State
  const [liveFeedEvents, setLiveFeedEvents] = useState([]);
  const [liveFeedPaused, setLiveFeedPaused] = useState(false);
  const [liveFeedConnected, setLiveFeedConnected] = useState(false);
  const wsRef = useRef(null);
  
  // Archive State
  const [archiveLogs, setArchiveLogs] = useState([]);
  const [archiveLoading, setArchiveLoading] = useState(false);
  
  // Retention Status
  const [retentionStatus, setRetentionStatus] = useState(null);
  
  // Filters
  const [filters, setFilters] = useState({
    date_from: '',
    date_to: '',
    actor_type: '',
    action: '',
    entity_type: '',
    severity: '',
    status: '',
    app_source: '',
    search: ''
  });
  
  const [showFilters, setShowFilters] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  const fetchLogs = useCallback(async () => {
    try {
      setLoading(true);
      
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        ...Object.fromEntries(
          Object.entries(filters).filter(([_, v]) => v !== '')
        )
      };
      
      const response = await api.get('/audit-logs', { params });
      setLogs(response.data.logs || []);
      setPagination(prev => ({
        ...prev,
        total: response.data.pagination.total,
        total_pages: response.data.pagination.total_pages
      }));
    } catch (error) {
      console.error('Error fetching logs:', error);
      toast.error('Gabim gjatë marrjes së logs');
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, filters]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await api.get('/audit-logs/stats', { params: { period: 'today' } });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }, []);

  useEffect(() => {
    fetchLogs();
    fetchStats();
    fetchRetentionStatus();
  }, [fetchLogs, fetchStats]);
  
  // WebSocket Live Feed Connection
  useEffect(() => {
    if (activeTab === 'live-feed' && !liveFeedPaused) {
      connectLiveFeed();
    }
    
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [activeTab, liveFeedPaused]);
  
  const connectLiveFeed = () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    
    const wsUrl = process.env.REACT_APP_BACKEND_URL?.replace('https://', 'wss://').replace('http://', 'ws://');
    const ws = new WebSocket(`${wsUrl}/ws/admin/live-feed?token=${token}`);
    
    ws.onopen = () => {
      setLiveFeedConnected(true);
      toast.success('Live Feed i lidhur');
    };
    
    ws.onmessage = (event) => {
      const data = JSON.parse(event.data);
      
      if (data.type === 'audit.live_feed') {
        setLiveFeedEvents(prev => [data.event, ...prev].slice(0, 100));
      } else if (data.type === 'heartbeat') {
        // Heartbeat - connection alive
      } else if (data.type === 'connection.established') {
        console.log('Live feed connected:', data.message);
      }
    };
    
    ws.onclose = () => {
      setLiveFeedConnected(false);
    };
    
    ws.onerror = (error) => {
      console.error('WebSocket error:', error);
      setLiveFeedConnected(false);
    };
    
    wsRef.current = ws;
  };
  
  const toggleLiveFeedPause = () => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(liveFeedPaused ? 'resume' : 'pause');
      setLiveFeedPaused(!liveFeedPaused);
    }
  };
  
  // Format action to readable Albanian text
  const formatAction = (action) => {
    if (!action) return 'Veprim i panjohur';
    
    // Action translation map
    const actionMap = {
      // Exchange Rate
      'api.put.api.settings.exchange-rate': 'Ndryshoi kursin e këmbimit',
      'api.get.api.settings.exchange-rate': 'Shikoi kursin e këmbimit',
      'api.get.api.settings.exchange-rate.history': 'Shikoi historikun e kursit',
      
      // Auth
      'api.post.api.auth.login': 'Hyri në sistem',
      'api.post.api.auth.logout': 'Doli nga sistemi',
      'api.post.api.auth.register': 'U regjistrua',
      
      // Drivers
      'api.get.api.drivers': 'Shikoi listën e shoferëve',
      'api.post.api.drivers': 'Shtoi shofer të ri',
      'api.put.api.drivers': 'Përditësoi të dhënat e shoferit',
      'api.delete.api.drivers': 'Fshiu shofer',
      'api.post.api.drivers.reset-password': 'Rivendosi fjalëkalimin e shoferit',
      
      // Passengers
      'api.get.api.passengers': 'Shikoi listën e pasagjerëve',
      'api.post.api.passengers': 'Shtoi pasagjer të ri',
      'api.put.api.passengers': 'Përditësoi të dhënat e pasagjerit',
      'api.delete.api.passengers': 'Fshiu pasagjer',
      
      // Rides
      'api.get.api.rides': 'Shikoi udhëtimet',
      'api.post.api.rides': 'Krijoi udhëtim të ri',
      'api.put.api.rides': 'Përditësoi udhëtimin',
      'api.post.api.rides.create': 'Krijoi porosi të re',
      'api.post.api.rides.accept': 'Pranoi udhëtimin',
      'api.post.api.rides.complete': 'Përfundoi udhëtimin',
      'api.post.api.rides.cancel': 'Anuloi udhëtimin',
      
      // Shifts
      'api.get.api.shifts': 'Shikoi turnet',
      'api.post.api.shifts': 'Filloi turn të ri',
      'api.post.api.shifts.verify': 'Verifikoi turnin',
      'api.post.api.shifts.close': 'Mbylli turnin',
      
      // Fleet
      'api.get.api.fleet': 'Shikoi flotën',
      'api.post.api.fleet': 'Shtoi automjet',
      'api.put.api.fleet': 'Përditësoi automjetin',
      
      // Settings
      'api.get.api.settings': 'Shikoi cilësimet',
      'api.put.api.settings': 'Përditësoi cilësimet',
      'api.post.api.settings': 'Ruajti cilësimet',
      
      // Payments
      'api.get.api.payments': 'Shikoi pagesat',
      'api.post.api.payments': 'Regjistroi pagesë',
      
      // Invoices
      'api.get.api.invoices': 'Shikoi faturat',
      'api.post.api.invoices': 'Krijoi faturë'
    };
    
    // Check for exact match
    if (actionMap[action]) {
      return actionMap[action];
    }
    
    // Try to match with dynamic parts (like IDs)
    const normalizedAction = action.replace(/\/[a-f0-9-]{36}/gi, '').replace(/\/\d+/g, '');
    if (actionMap[normalizedAction]) {
      return actionMap[normalizedAction];
    }
    
    // Parse action to generate readable text
    const parts = action.split('.');
    if (parts.length >= 3) {
      const method = parts[1]?.toUpperCase();
      const endpoint = parts.slice(2).join('/').replace(/-/g, ' ');
      
      const methodMap = {
        'GET': 'Shikoi',
        'POST': 'Krijoi/Shtoi',
        'PUT': 'Përditësoi',
        'DELETE': 'Fshiu',
        'PATCH': 'Modifikoi'
      };
      
      return `${methodMap[method] || method} ${endpoint}`;
    }
    
    return action;
  };
  
  const fetchRetentionStatus = async () => {
    try {
      const response = await api.get('/audit-logs/retention/status');
      setRetentionStatus(response.data);
    } catch (error) {
      console.error('Error fetching retention status:', error);
    }
  };
  
  const fetchArchiveLogs = async () => {
    try {
      setArchiveLoading(true);
      const params = {
        skip: 0,
        limit: 100,
        ...Object.fromEntries(
          Object.entries(filters).filter(([_, v]) => v !== '')
        )
      };
      
      const response = await api.get('/audit-logs/archived', { params });
      setArchiveLogs(response.data.logs || []);
    } catch (error) {
      console.error('Error fetching archive logs:', error);
      toast.error('Gabim gjatë marrjes së archive logs');
    } finally {
      setArchiveLoading(false);
    }
  };
  
  useEffect(() => {
    if (activeTab === 'archive') {
      fetchArchiveLogs();
    }
  }, [activeTab]);

  const handleExport = async () => {
    try {
      const response = await api.get('/audit-logs/export', {
        params: {
          format: 'csv',
          date_from: filters.date_from,
          date_to: filters.date_to,
          limit: 10000
        },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `audit_logs_${format(new Date(), 'yyyyMMdd_HHmmss')}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Logs u eksportuan me sukses');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Gabim gjatë eksportimit');
    }
  };

  const getSeverityBadge = (severity) => {
    const badges = {
      info: { bg: 'bg-blue-100', text: 'text-blue-700', icon: Info },
      warning: { bg: 'bg-yellow-100', text: 'text-yellow-700', icon: AlertTriangle },
      critical: { bg: 'bg-red-100', text: 'text-red-700', icon: AlertCircle }
    };
    const badge = badges[severity] || badges.info;
    const Icon = badge.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}>
        <Icon className="w-3 h-3" />
        {severity}
      </span>
    );
  };

  const getStatusBadge = (status) => {
    if (status === 'success') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
          <CheckCircle2 className="w-3 h-3" />
          success
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
        <XCircle className="w-3 h-3" />
        fail
      </span>
    );
  };

  const getActorIcon = (actorType) => {
    const icons = {
      admin: Shield,
      dispatcher: Users,
      driver: Car,
      passenger: User,
      system: Server
    };
    return icons[actorType] || User;
  };

  const getAppSourceBadge = (source) => {
    const sources = {
      admin_web: { label: 'Admin Web', color: 'bg-purple-100 text-purple-700' },
      dispatcher_web: { label: 'Dispatcher', color: 'bg-blue-100 text-blue-700' },
      driver_app: { label: 'Driver App', color: 'bg-green-100 text-green-700' },
      passenger_app: { label: 'Passenger App', color: 'bg-orange-100 text-orange-700' },
      api: { label: 'API', color: 'bg-gray-100 text-gray-700' },
      system: { label: 'System', color: 'bg-slate-100 text-slate-700' }
    };
    const s = sources[source] || sources.api;
    return <span className={`px-2 py-0.5 rounded text-xs font-medium ${s.color}`}>{s.label}</span>;
  };

  const formatTimestamp = (ts) => {
    if (!ts) return '--';
    try {
      const date = typeof ts === 'string' ? parseISO(ts) : new Date(ts);
      return format(date, 'dd MMM yyyy, HH:mm:ss', { locale: sq });
    } catch {
      return ts;
    }
  };

  const openLogDetail = (log) => {
    setSelectedLog(log);
    setShowDetailModal(true);
  };

  return (
    <div className="p-6 space-y-6" data-testid="audit-logs-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Audit Logs</h1>
          <p className="text-gray-500 text-sm">Gjurmimi i të gjitha veprimeve në sistem</p>
        </div>
        
        <div className="flex items-center gap-3">
          <button
            onClick={handleExport}
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium transition"
          >
            <Download className="w-4 h-4" />
            Eksporto CSV
          </button>
          <button
            onClick={() => { fetchLogs(); fetchStats(); }}
            className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium transition"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Rifresko
          </button>
        </div>
      </div>
      
      {/* Tabs */}
      <div className="flex items-center gap-2 border-b border-gray-200">
        <button
          onClick={() => setActiveTab('recent')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition ${
            activeTab === 'recent' 
              ? 'border-red-600 text-red-600' 
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          <div className="flex items-center gap-2">
            <Activity className="w-4 h-4" />
            Recent Logs
          </div>
        </button>
        
        <button
          onClick={() => setActiveTab('live-feed')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition ${
            activeTab === 'live-feed' 
              ? 'border-red-600 text-red-600' 
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          <div className="flex items-center gap-2">
            <Radio className={`w-4 h-4 ${liveFeedConnected ? 'text-green-500 animate-pulse' : ''}`} />
            Live Feed
            {liveFeedConnected && (
              <span className="w-2 h-2 bg-green-500 rounded-full"></span>
            )}
          </div>
        </button>
        
        <button
          onClick={() => setActiveTab('archive')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition ${
            activeTab === 'archive' 
              ? 'border-red-600 text-red-600' 
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          <div className="flex items-center gap-2">
            <Archive className="w-4 h-4" />
            Arkiva (&gt;180 ditë)
          </div>
        </button>
        
        {retentionStatus && (
          <div className="ml-auto text-xs text-gray-500 flex items-center gap-4">
            <span>Hot: {retentionStatus.hot_storage?.count?.toLocaleString() || 0}</span>
            <span>Archive: {retentionStatus.archive_storage?.count?.toLocaleString() || 0}</span>
          </div>
        )}
      </div>
      
      {/* Live Feed Tab */}
      {activeTab === 'live-feed' && (
        <div className="space-y-4">
          {/* Live Feed Controls */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className={`flex items-center gap-2 ${liveFeedConnected ? 'text-green-600' : 'text-gray-400'}`}>
                  {liveFeedConnected ? <Wifi className="w-5 h-5" /> : <WifiOff className="w-5 h-5" />}
                  <span className="text-sm font-medium">
                    {liveFeedConnected ? 'I lidhur' : 'I shkëputur'}
                  </span>
                </div>
                
                {liveFeedConnected && (
                  <button
                    onClick={toggleLiveFeedPause}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition ${
                      liveFeedPaused 
                        ? 'bg-green-100 text-green-700 hover:bg-green-200' 
                        : 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'
                    }`}
                  >
                    {liveFeedPaused ? (
                      <>
                        <Play className="w-4 h-4" />
                        Vazhdo
                      </>
                    ) : (
                      <>
                        <Pause className="w-4 h-4" />
                        Ndalo
                      </>
                    )}
                  </button>
                )}
              </div>
              
              <div className="text-sm text-gray-500">
                {liveFeedEvents.length} evente kritike
              </div>
            </div>
          </div>
          
          {/* Live Feed Events */}
          <div className="bg-gray-900 rounded-xl border border-gray-700 shadow-sm overflow-hidden">
            <div className="p-3 border-b border-gray-700 flex items-center gap-2">
              <Zap className="w-4 h-4 text-yellow-400" />
              <span className="text-sm font-medium text-white">Critical Events (Warning/Critical only)</span>
            </div>
            
            <div className="divide-y divide-gray-700 max-h-[600px] overflow-y-auto">
              {liveFeedEvents.length === 0 ? (
                <div className="p-8 text-center text-gray-400">
                  <Bell className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p>Duke pritur evente kritike...</p>
                  <p className="text-xs mt-1">Vetëm warning dhe critical shfaqen këtu</p>
                </div>
              ) : (
                liveFeedEvents.map((event, idx) => (
                  <div 
                    key={event.id || idx} 
                    className={`p-4 hover:bg-gray-800 transition cursor-pointer ${
                      event.severity === 'critical' ? 'border-l-4 border-red-500' : 'border-l-4 border-yellow-500'
                    }`}
                    onClick={() => openLogDetail(event)}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          {event.severity === 'critical' ? (
                            <AlertCircle className="w-4 h-4 text-red-400" />
                          ) : (
                            <AlertTriangle className="w-4 h-4 text-yellow-400" />
                          )}
                          <span className="text-sm text-blue-300">{formatAction(event.action)}</span>
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                            event.severity === 'critical' 
                              ? 'bg-red-900/50 text-red-300' 
                              : 'bg-yellow-900/50 text-yellow-300'
                          }`}>
                            {event.severity}
                          </span>
                        </div>
                        <p className="text-white text-sm">{event.summary}</p>
                        <div className="flex items-center gap-3 mt-2 text-xs text-gray-400">
                          <span>{event.actor_name || event.actor_type}</span>
                          <span>•</span>
                          <span>{event.entity_type}{event.entity_id && `:${event.entity_id.slice(0, 8)}`}</span>
                          {event.correlation_id && (
                            <>
                              <span>•</span>
                              <span className="font-mono">#{event.correlation_id}</span>
                            </>
                          )}
                        </div>
                      </div>
                      <div className="text-xs text-gray-400 whitespace-nowrap">
                        {formatTimestamp(event.ts)}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
      
      {/* Archive Tab */}
      {activeTab === 'archive' && (
        <div className="space-y-4">
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-gray-600">
                <Archive className="w-5 h-5" />
                <span className="text-sm">Logs më të vjetra se 180 ditë</span>
              </div>
              <button
                onClick={fetchArchiveLogs}
                className="flex items-center gap-2 px-3 py-1.5 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium transition"
              >
                <RefreshCw className={`w-4 h-4 ${archiveLoading ? 'animate-spin' : ''}`} />
                Rifresko
              </button>
            </div>
          </div>
          
          {/* Archive Filters - Same as Recent */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Nga Data</label>
                <input
                  type="date"
                  value={filters.date_from}
                  onChange={(e) => setFilters({...filters, date_from: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Deri Data</label>
                <input
                  type="date"
                  value={filters.date_to}
                  onChange={(e) => setFilters({...filters, date_to: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Action</label>
                <input
                  type="text"
                  placeholder="p.sh. auth.login"
                  value={filters.action}
                  onChange={(e) => setFilters({...filters, action: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                />
              </div>
              <div className="flex items-end">
                <button
                  onClick={fetchArchiveLogs}
                  className="w-full px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700"
                >
                  Kërko në Arkivë
                </button>
              </div>
            </div>
          </div>
          
          {/* Archive Logs Table */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Koha</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Aktori</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Action</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Entity</th>
                    <th className="text-center px-4 py-3 text-xs font-medium text-gray-500 uppercase">Severity</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Arkivuar</th>
                    <th className="text-center px-4 py-3 text-xs font-medium text-gray-500 uppercase">Detaje</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {archiveLoading ? (
                    <tr>
                      <td colSpan={7} className="px-4 py-12 text-center text-gray-500">
                        <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
                        Duke ngarkuar arkivën...
                      </td>
                    </tr>
                  ) : archiveLogs.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-4 py-12 text-center text-gray-500">
                        <Archive className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                        Nuk ka logs të arkivuara
                      </td>
                    </tr>
                  ) : (
                    archiveLogs.map((log) => (
                      <tr key={log.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-600">{formatTimestamp(log.ts)}</td>
                        <td className="px-4 py-3 text-sm">{log.actor_name || log.actor_type}</td>
                        <td className="px-4 py-3">
                          <span className="text-xs text-gray-600">{formatAction(log.action)}</span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {log.entity_type}{log.entity_id && `:${log.entity_id.slice(0, 8)}`}
                        </td>
                        <td className="px-4 py-3 text-center">{getSeverityBadge(log.severity)}</td>
                        <td className="px-4 py-3 text-sm text-gray-500">{formatTimestamp(log.archived_at)}</td>
                        <td className="px-4 py-3 text-center">
                          <button
                            onClick={() => openLogDetail(log)}
                            className="p-1.5 hover:bg-gray-100 rounded-lg transition"
                          >
                            <Eye className="w-4 h-4 text-gray-500" />
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
      
      {/* Recent Logs Tab (Original Content) */}
      {activeTab === 'recent' && (
        <>
          {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-4">
          <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
            <div className="flex items-center gap-2 mb-1">
              <Activity className="w-4 h-4 text-blue-500" />
              <span className="text-xs text-gray-500">Total Sot</span>
            </div>
            <p className="text-xl font-bold text-gray-900">{stats.total_logs?.toLocaleString() || 0}</p>
          </div>
          
          <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
            <div className="flex items-center gap-2 mb-1">
              <XCircle className="w-4 h-4 text-red-500" />
              <span className="text-xs text-gray-500">Dështime</span>
            </div>
            <p className="text-xl font-bold text-red-600">{stats.failed_actions || 0}</p>
          </div>
          
          <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
            <div className="flex items-center gap-2 mb-1">
              <AlertTriangle className="w-4 h-4 text-orange-500" />
              <span className="text-xs text-gray-500">Kritike</span>
            </div>
            <p className="text-xl font-bold text-orange-600">{stats.critical_events || 0}</p>
          </div>
          
          <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
            <div className="flex items-center gap-2 mb-1">
              <Shield className="w-4 h-4 text-purple-500" />
              <span className="text-xs text-gray-500">Admin</span>
            </div>
            <p className="text-xl font-bold text-gray-900">{stats.by_actor_type?.admin || 0}</p>
          </div>
          
          <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
            <div className="flex items-center gap-2 mb-1">
              <Car className="w-4 h-4 text-green-500" />
              <span className="text-xs text-gray-500">Driver</span>
            </div>
            <p className="text-xl font-bold text-gray-900">{stats.by_actor_type?.driver || 0}</p>
          </div>
          
          <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
            <div className="flex items-center gap-2 mb-1">
              <Server className="w-4 h-4 text-slate-500" />
              <span className="text-xs text-gray-500">System</span>
            </div>
            <p className="text-xl font-bold text-gray-900">{stats.by_actor_type?.system || 0}</p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2 text-sm font-medium text-gray-700"
          >
            <Filter className="w-4 h-4" />
            Filtrat
            <ChevronDown className={`w-4 h-4 transition ${showFilters ? 'rotate-180' : ''}`} />
          </button>
          
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                placeholder="Kërko..."
                value={filters.search}
                onChange={(e) => setFilters({...filters, search: e.target.value})}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-red-500 w-64"
              />
            </div>
          </div>
        </div>
        
        {showFilters && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 pt-4 border-t border-gray-200">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Nga Data</label>
              <input
                type="date"
                value={filters.date_from}
                onChange={(e) => setFilters({...filters, date_from: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              />
            </div>
            
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Deri Data</label>
              <input
                type="date"
                value={filters.date_to}
                onChange={(e) => setFilters({...filters, date_to: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              />
            </div>
            
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Tipi Aktorit</label>
              <select
                value={filters.actor_type}
                onChange={(e) => setFilters({...filters, actor_type: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              >
                <option value="">Të gjithë</option>
                <option value="admin">Admin</option>
                <option value="dispatcher">Dispatcher</option>
                <option value="driver">Driver</option>
                <option value="passenger">Passenger</option>
                <option value="system">System</option>
              </select>
            </div>
            
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Severity</label>
              <select
                value={filters.severity}
                onChange={(e) => setFilters({...filters, severity: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              >
                <option value="">Të gjitha</option>
                <option value="info">Info</option>
                <option value="warning">Warning</option>
                <option value="critical">Critical</option>
              </select>
            </div>
            
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Status</label>
              <select
                value={filters.status}
                onChange={(e) => setFilters({...filters, status: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              >
                <option value="">Të gjitha</option>
                <option value="success">Success</option>
                <option value="fail">Fail</option>
              </select>
            </div>
            
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">App Source</label>
              <select
                value={filters.app_source}
                onChange={(e) => setFilters({...filters, app_source: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              >
                <option value="">Të gjitha</option>
                <option value="admin_web">Admin Web</option>
                <option value="dispatcher_web">Dispatcher Web</option>
                <option value="driver_app">Driver App</option>
                <option value="passenger_app">Passenger App</option>
                <option value="api">API</option>
                <option value="system">System</option>
              </select>
            </div>
            
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Action</label>
              <input
                type="text"
                placeholder="p.sh. auth.login"
                value={filters.action}
                onChange={(e) => setFilters({...filters, action: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              />
            </div>
            
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Entity Type</label>
              <select
                value={filters.entity_type}
                onChange={(e) => setFilters({...filters, entity_type: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              >
                <option value="">Të gjitha</option>
                <option value="ride">Ride</option>
                <option value="driver">Driver</option>
                <option value="vehicle">Vehicle</option>
                <option value="passenger">Passenger</option>
                <option value="user">User</option>
                <option value="settings">Settings</option>
              </select>
            </div>
            
            <div className="flex items-end">
              <button
                onClick={() => setFilters({
                  date_from: format(subDays(new Date(), 1), 'yyyy-MM-dd'),
                  date_to: format(new Date(), 'yyyy-MM-dd'),
                  actor_type: '',
                  action: '',
                  entity_type: '',
                  severity: '',
                  status: '',
                  app_source: '',
                  search: ''
                })}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 border border-gray-300 rounded-lg"
              >
                Pastro Filtrat
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Logs Table */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Koha</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Aktori</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Source</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Action</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Entity</th>
                <th className="text-center px-4 py-3 text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="text-center px-4 py-3 text-xs font-medium text-gray-500 uppercase">Severity</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">IP</th>
                <th className="text-center px-4 py-3 text-xs font-medium text-gray-500 uppercase">Detaje</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr>
                  <td colSpan={9} className="px-4 py-12 text-center text-gray-500">
                    <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
                    Duke ngarkuar...
                  </td>
                </tr>
              ) : logs.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-4 py-12 text-center text-gray-500">
                    <Database className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                    Nuk ka logs për filtrat e zgjedhura
                  </td>
                </tr>
              ) : (
                logs.map((log) => {
                  const ActorIcon = getActorIcon(log.actor_type);
                  return (
                    <tr key={log.id} className={`hover:bg-gray-50 ${log.severity === 'critical' ? 'bg-red-50/50' : ''}`}>
                      <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                        <div className="flex items-center gap-1">
                          <Clock className="w-3 h-3 text-gray-400" />
                          {formatTimestamp(log.ts)}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-7 rounded-full bg-gray-100 flex items-center justify-center">
                            <ActorIcon className="w-4 h-4 text-gray-600" />
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-900">{log.actor_name || log.actor_type}</p>
                            <p className="text-xs text-gray-500">{log.role || log.actor_type}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        {getAppSourceBadge(log.app_source)}
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-gray-700">
                          {formatAction(log.action)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {log.entity_type && (
                          <span>
                            {log.entity_type}
                            {log.entity_id && <span className="text-gray-400">:{log.entity_id.slice(0, 8)}</span>}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        {getStatusBadge(log.status)}
                      </td>
                      <td className="px-4 py-3 text-center">
                        {getSeverityBadge(log.severity)}
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-500 font-mono">
                        {log.ip_address || '--'}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <button
                          onClick={() => openLogDetail(log)}
                          className="p-1.5 hover:bg-gray-100 rounded-lg transition"
                          title="Shiko Detajet"
                        >
                          <Eye className="w-4 h-4 text-gray-500" />
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        
        {/* Pagination */}
        {pagination.total > 0 && (
          <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between">
            <p className="text-sm text-gray-500">
              Duke shfaqur {((pagination.page - 1) * pagination.limit) + 1} - {Math.min(pagination.page * pagination.limit, pagination.total)} nga {pagination.total} logs
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                disabled={pagination.page <= 1}
                className="p-2 hover:bg-gray-100 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="text-sm text-gray-600">
                Faqja {pagination.page} nga {pagination.total_pages}
              </span>
              <button
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                disabled={pagination.page >= pagination.total_pages}
                className="p-2 hover:bg-gray-100 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
      
      {/* End of Recent Tab */}
      </>
      )}

      {/* Detail Modal */}
      {showDetailModal && selectedLog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Detajet e Log</h3>
              <button 
                onClick={() => setShowDetailModal(false)}
                className="p-2 hover:bg-gray-100 rounded-full transition"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            
            <div className="p-4 overflow-y-auto max-h-[70vh]">
              <div className="space-y-4">
                {/* Basic Info */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-medium text-gray-500">Request ID</label>
                    <p className="text-sm font-mono text-gray-900">{selectedLog.request_id || '--'}</p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-500">Timestamp</label>
                    <p className="text-sm text-gray-900">{formatTimestamp(selectedLog.ts)}</p>
                  </div>
                </div>
                
                {/* Actor */}
                <div className="bg-gray-50 rounded-lg p-3">
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">Aktori</h4>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="text-gray-500">Tipi:</span> {selectedLog.actor_type}
                    </div>
                    <div>
                      <span className="text-gray-500">ID:</span> {selectedLog.actor_id || '--'}
                    </div>
                    <div>
                      <span className="text-gray-500">Emri:</span> {selectedLog.actor_name || '--'}
                    </div>
                    <div>
                      <span className="text-gray-500">Roli:</span> {selectedLog.role || '--'}
                    </div>
                  </div>
                </div>
                
                {/* Action & Entity */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-medium text-gray-500">Veprimi</label>
                    <p className="text-sm text-gray-900 mt-1">{formatAction(selectedLog.action)}</p>
                    <code className="block text-xs bg-gray-100 px-2 py-1 rounded mt-1 text-gray-500">{selectedLog.action}</code>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-500">Entity</label>
                    <p className="text-sm text-gray-900">{selectedLog.entity_type}:{selectedLog.entity_id}</p>
                  </div>
                </div>
                
                {/* Status & Severity */}
                <div className="flex items-center gap-4">
                  <div>
                    <label className="text-xs font-medium text-gray-500 mb-1 block">Status</label>
                    {getStatusBadge(selectedLog.status)}
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-500 mb-1 block">Severity</label>
                    {getSeverityBadge(selectedLog.severity)}
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-500 mb-1 block">App Source</label>
                    {getAppSourceBadge(selectedLog.app_source)}
                  </div>
                </div>
                
                {/* Request Info */}
                <div className="bg-gray-50 rounded-lg p-3">
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">Request Info</h4>
                  <div className="space-y-2 text-sm">
                    <div>
                      <span className="text-gray-500">IP Address:</span> {selectedLog.ip_address || '--'}
                    </div>
                    <div>
                      <span className="text-gray-500">User Agent:</span>
                      <p className="text-xs text-gray-600 break-all">{selectedLog.user_agent || '--'}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Device ID:</span> {selectedLog.device_id || '--'}
                    </div>
                  </div>
                </div>
                
                {/* Metadata */}
                {selectedLog.metadata && Object.keys(selectedLog.metadata).length > 0 && (
                  <div>
                    <label className="text-xs font-medium text-gray-500 mb-1 block">Metadata</label>
                    <pre className="text-xs bg-gray-900 text-green-400 p-3 rounded-lg overflow-x-auto">
                      {JSON.stringify(selectedLog.metadata, null, 2)}
                    </pre>
                  </div>
                )}
                
                {/* Before/After */}
                {(selectedLog.before_data || selectedLog.after_data) && (
                  <div className="grid grid-cols-2 gap-4">
                    {selectedLog.before_data && (
                      <div>
                        <label className="text-xs font-medium text-gray-500 mb-1 block">Before</label>
                        <pre className="text-xs bg-red-50 text-red-800 p-3 rounded-lg overflow-x-auto">
                          {JSON.stringify(selectedLog.before_data, null, 2)}
                        </pre>
                      </div>
                    )}
                    {selectedLog.after_data && (
                      <div>
                        <label className="text-xs font-medium text-gray-500 mb-1 block">After</label>
                        <pre className="text-xs bg-green-50 text-green-800 p-3 rounded-lg overflow-x-auto">
                          {JSON.stringify(selectedLog.after_data, null, 2)}
                        </pre>
                      </div>
                    )}
                  </div>
                )}
                
                {/* Error Info */}
                {selectedLog.error_message && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                    <h4 className="text-sm font-semibold text-red-700 mb-1">Error</h4>
                    <p className="text-sm text-red-600">{selectedLog.error_message}</p>
                    {selectedLog.error_code && (
                      <p className="text-xs text-red-500 mt-1">Code: {selectedLog.error_code}</p>
                    )}
                  </div>
                )}
              </div>
            </div>
            
            <div className="p-4 border-t border-gray-200 flex justify-end">
              <button
                onClick={() => setShowDetailModal(false)}
                className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium transition"
              >
                Mbyll
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AuditLogsPage;
